import { FC, useState } from 'react';
import { Dialog } from 'src/components/widgets/dialog';
import { Icon } from 'src/components/widgets/icon';
import { NotificationBanner } from 'src/components/widgets/notificationBanner';
import { T } from 'src/shared/helpers';
import CreditCardIcon from 'src/shared/material_icons/action/credit_card.svg';
import InfoIcon from 'src/shared/material_icons/action/info.svg';
import ArrowForwardIcon from 'src/shared/material_icons/navigation/arrow_forward.svg';
import styled from 'styled-components';
import { SwitchAcknowledgeFooter } from './switchAcknowledgeFooter';
import { SwitchDialogWindow } from './switchDialogWindow';
import { SwitchWizardStep, SwitchWizardStepCount, SwitchWizardStepIndicator } from './switchWizardSteps';

export interface CustomPlanFinancialPreview {
    isEstimate: boolean;
    currentPlan: {
        monthlyPrice: number;
        currencyCode: string;
    };
    newPlan: {
        monthlyPrice: number;
        currencyCode: string;
    };
    proration: {
        available: boolean;
        remainingDays: number;
    };
    paymentOnPlanChange: {
        estimatedCreditApplied: number;
        estimatedRefundValue: number;
        currencyCode: string;
        nextRenewalAtUtc: string | null;
        nextRenewalAmount: number;
    };
    warnings: readonly string[];
}

interface CustomPlanFinancialPreviewDialogProps {
    preview: CustomPlanFinancialPreview;
    paymentMethodSetupRequired: boolean;
    onContinue: () => void;
    /** Optional override for the dialog's close (X). Defaults to just closing this dialog. */
    onClose?: () => void;
    stepCount?: SwitchWizardStepCount;
}

/** Fills the fixed-height window with the footer pinned at the bottom; only the content area scrolls. */
const Layout = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
`;

const Header = styled.div`
    margin-bottom: 24px;
`;

const Title = styled.h2`
    margin: 0 0 4px;
    font-size: 16px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
`;

const Subtitle = styled.p`
    margin: 0;
    font-size: 13px;
    color: #6b6b6b;
`;

const WizardContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const ContentArea = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

/* ---------- Plan comparison ---------- */

const PlanRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const PlanCard = styled.div<{ $isNew?: boolean }>`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 6px;
    padding: 12px 16px;
    border-radius: 8px;
    border: 1px solid ${(props) => (props.$isNew ? '#008556' : props.theme.app.borderColor)};
    background-color: ${(props) => (props.$isNew ? '#E8F5EE' : props.theme.app.backgroundColor)};
`;

const PlanLabel = styled.span<{ $isNew?: boolean }>`
    font-size: 13px;
    color: ${(props) => (props.$isNew ? '#008556' : '#6b6b6b')};
    font-weight: ${(props) => (props.$isNew ? 600 : 400)};
`;

const PlanPrice = styled.div`
    color: ${(props) => props.theme.app.textColor};
`;

const PlanAmount = styled.strong`
    font-size: 14px;
    font-weight: 700;
`;

const PlanPeriod = styled.span`
    font-size: 13px;
    color: #6b6b6b;
`;

const PlanArrow = styled(Icon)`
    flex-shrink: 0;
    color: #6b6b6b;

    svg {
        width: 20px;
        height: 20px;
    }
`;

const PlanChangeSentence = styled.p`
    margin: -8px 0 0;
    font-size: 13px;
    color: #6b6b6b;
    text-align: center;
`;

/* ---------- Details ---------- */

const DetailsCard = styled.div`
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
`;

const DetailRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    /* Shared min-height so single-line rows match the two-line "Next renewal" row. */
    min-height: 56px;
    padding: 8px 16px;
    color: ${(props) => props.theme.app.textColor};

    & + & {
        border-top: 1px solid ${(props) => props.theme.app.borderColor};
    }
`;

const DetailLabel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 13px;
`;

const DetailSubLabel = styled.span`
    font-size: 12px;
    color: #6b6b6b;
`;

const DetailValue = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: flex-end;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    text-align: right;
`;

const EstimateStrip = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    border-top: 1px solid ${(props) => props.theme.app.borderColor};
    font-size: 12px;
    color: ${(props) => props.theme.app.textColor};

    p {
        margin: 0;
    }
`;

const EstimateIcon = styled(Icon)`
    flex-shrink: 0;
    color: #0069d3;

    svg {
        width: 16px;
        height: 16px;
    }
`;

/* ---------- Please note ---------- */

const PleaseNote = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const PleaseNoteHeading = styled.span`
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #6b6b6b;
`;

const PleaseNoteList = styled.ul`
    margin: 0;
    padding-left: 20px;
    font-size: 13px;
    line-height: 20px;
    color: #6b6b6b;

    li + li {
        margin-top: 4px;
    }
`;

// Top-align the banner's icon with the title (the shared banner centres it against the whole content).
const PaymentBanner = styled(NotificationBanner)`
    & > div {
        align-items: flex-start;
    }
`;

const formatAmount = (amount: number, currencyCode: string): string => `${currencyCode} ${amount.toFixed(2)}`;
const hasAmount = (amount: number): boolean => Math.abs(amount) >= 0.005;

const CustomPlanFinancialPreviewDialog: FC<CustomPlanFinancialPreviewDialogProps> = ({
    preview,
    paymentMethodSetupRequired,
    onContinue,
    onClose,
    stepCount
}) => {
    const [hasAcknowledgedInfo, setHasAcknowledgedInfo] = useState(false);
    const { currentPlan, newPlan, paymentOnPlanChange } = preview;
    const hasCredit = hasAmount(paymentOnPlanChange.estimatedCreditApplied);
    const hasRefund = hasAmount(paymentOnPlanChange.estimatedRefundValue);
    const refundPeriodLabel = preview.proration.available
        ? preview.proration.remainingDays === 1
            ? T._('For 1 remaining day', 'Admin')
            : T._('For {0} remaining days', 'Admin').replace('{0}', preview.proration.remainingDays.toString())
        : null;
    const nextRenewalDate = paymentOnPlanChange.nextRenewalAtUtc
        ? new Date(paymentOnPlanChange.nextRenewalAtUtc).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })
        : null;
    const visibleWarnings = preview.warnings.slice(1, -1);

    return (
        <Dialog windowDecorator={SwitchDialogWindow} onClose={onClose}>
            <Layout>
                <Header>
                    <SwitchWizardStepIndicator step={SwitchWizardStep.Subscription} stepCount={stepCount} />
                    <Title>{T._('Financial preview', 'Admin')}</Title>
                    <Subtitle>{T._('Here is how this subscription change will affect you.', 'Admin')}</Subtitle>
                </Header>
                <WizardContainer>
                    <ContentArea>
                        <Content>
                            <PlanRow>
                                <PlanCard>
                                    <PlanLabel>{T._('Current plan', 'Admin')}</PlanLabel>
                                    <PlanPrice>
                                        <PlanAmount>{formatAmount(currentPlan.monthlyPrice, currentPlan.currencyCode)}</PlanAmount>
                                        <PlanPeriod> {T._('/ month', 'Admin')}</PlanPeriod>
                                    </PlanPrice>
                                </PlanCard>
                                <PlanArrow>
                                    <ArrowForwardIcon />
                                </PlanArrow>
                                <PlanCard $isNew>
                                    <PlanLabel $isNew>{T._('New plan', 'Admin')}</PlanLabel>
                                    <PlanPrice>
                                        <PlanAmount>{formatAmount(newPlan.monthlyPrice, newPlan.currencyCode)}</PlanAmount>
                                        <PlanPeriod> {T._('/ month', 'Admin')}</PlanPeriod>
                                    </PlanPrice>
                                </PlanCard>
                            </PlanRow>
                            <PlanChangeSentence>
                                {T._('Your monthly plan will change from {0} to {1}.', 'Admin')
                                    .replace('{0}', formatAmount(currentPlan.monthlyPrice, currentPlan.currencyCode))
                                    .replace('{1}', formatAmount(newPlan.monthlyPrice, newPlan.currencyCode))}
                            </PlanChangeSentence>

                            <DetailsCard>
                                {hasRefund && (
                                    <DetailRow>
                                        <DetailLabel>
                                            <span>{T._('Refund', 'Admin')}</span>
                                            {refundPeriodLabel && <DetailSubLabel>{refundPeriodLabel}</DetailSubLabel>}
                                        </DetailLabel>
                                        <DetailValue>{formatAmount(paymentOnPlanChange.estimatedRefundValue, paymentOnPlanChange.currencyCode)}</DetailValue>
                                    </DetailRow>
                                )}
                                <DetailRow>
                                    <DetailLabel>
                                        <span>{nextRenewalDate ? T._('Next renewal', 'Admin') : T._('Monthly renewal', 'Admin')}</span>
                                        {nextRenewalDate && <DetailSubLabel>{nextRenewalDate}</DetailSubLabel>}
                                    </DetailLabel>
                                    <DetailValue>{formatAmount(paymentOnPlanChange.nextRenewalAmount, paymentOnPlanChange.currencyCode)}</DetailValue>
                                </DetailRow>
                                {hasCredit && (
                                    <DetailRow>
                                        <DetailLabel>{T._('Credit applied today', 'Admin')}</DetailLabel>
                                        <DetailValue>{formatAmount(paymentOnPlanChange.estimatedCreditApplied, paymentOnPlanChange.currencyCode)}</DetailValue>
                                    </DetailRow>
                                )}
                                {preview.isEstimate && (
                                    <EstimateStrip>
                                        <EstimateIcon>
                                            <InfoIcon />
                                        </EstimateIcon>
                                        <p>
                                            <strong>{T._('Estimated amounts.', 'Admin')}</strong>{' '}
                                            {T._('Final amounts may vary slightly when the subscription is processed.', 'Admin')}
                                        </p>
                                    </EstimateStrip>
                                )}
                            </DetailsCard>

                            {paymentMethodSetupRequired && (
                                <PaymentBanner
                                    type='WARNING'
                                    icon={<CreditCardIcon />}
                                    title={T._('Payment method required to continue', 'Admin')}
                                    content={
                                        <p>
                                            {T._(
                                                'After confirming this plan, you will need to set up a payment method. Once it is set up, the sales channel switch will start automatically.',
                                                'Admin'
                                            )}
                                        </p>
                                    }
                                />
                            )}

                            {visibleWarnings.length > 0 && (
                                <PleaseNote>
                                    <PleaseNoteHeading>{T._('Please note', 'Admin')}</PleaseNoteHeading>
                                    <PleaseNoteList>
                                        {visibleWarnings.map((warning) => (
                                            <li key={warning}>{warning}</li>
                                        ))}
                                    </PleaseNoteList>
                                </PleaseNote>
                            )}
                        </Content>
                    </ContentArea>
                    <SwitchAcknowledgeFooter checked={hasAcknowledgedInfo} onChange={setHasAcknowledgedInfo} onProceed={onContinue} />
                </WizardContainer>
            </Layout>
        </Dialog>
    );
};

export default CustomPlanFinancialPreviewDialog;
